import { TokenStatus } from "@/enum";
import { ROUTE_CONFIG } from "@/routes/config";
import { ERROR_TYPE } from "@/utils";
import { onboardingRedirectPath, resolveOnboardingVariant } from "@/utils/useOnboardingVariant";

import { SetPasswordResponse } from "../schema/set-password";

type SetPasswordFlow = "invitation" | "reset";

/**
 * The URL carried no `token` at all. Kept apart from the API's INVALID: a missing token is a
 * mangled link (PREC-4022: a mail gateway dropped everything after `?token`), which the user can
 * repair by copying the whole link, while INVALID is a token the server does not know.
 */
export const LINK_TOKEN_MISSING = "MISSING";

export type LinkProblem = TokenStatus | typeof LINK_TOKEN_MISSING;

const invitationStatusErrors = {
	[TokenStatus.INVALID]: ERROR_TYPE.SET_PASSWORD_LINK_INVALID,
	[TokenStatus.EXPIRED]: ERROR_TYPE.SET_PASSWORD_LINK_EXPIRED,
	[TokenStatus.USED]: ERROR_TYPE.SET_PASSWORD_LINK_USED,
	[LINK_TOKEN_MISSING]: ERROR_TYPE.SET_PASSWORD_LINK_MISSING,
} as const;

const resetStatusErrors = {
	[TokenStatus.INVALID]: ERROR_TYPE.RESET_PASSWORD_LINK_INVALID,
	[TokenStatus.EXPIRED]: ERROR_TYPE.RESET_PASSWORD_LINK_EXPIRED,
	[TokenStatus.USED]: ERROR_TYPE.RESET_PASSWORD_LINK_USED,
	[LINK_TOKEN_MISSING]: ERROR_TYPE.RESET_PASSWORD_LINK_MISSING,
} as const;

export const hasSetPasswordAuthPayload = (
	response: SetPasswordResponse
): response is SetPasswordResponse & Required<Pick<SetPasswordResponse, "accessToken" | "user">> =>
	Boolean(response.accessToken && response.user);

/**
 * Where accepting an invite lands. Which onboarding the new user owes is derived
 * from the variant on the response (PREC-3719), so a teammate joining a
 * workspace that is already set up goes straight to the slim flow instead of
 * bouncing through the full wizard's shell.
 */
export const getSetPasswordRedirectPath = (response: SetPasswordResponse): string | null => {
	if (!hasSetPasswordAuthPayload(response)) {
		return null;
	}

	return onboardingRedirectPath(resolveOnboardingVariant(response.user)) ?? ROUTE_CONFIG.LISTS;
};

/**
 * A token status that should block the SetPassword form and show a notice instead. `OPEN` (and an
 * absent status, e.g. while the validate request is in flight) is non-blocking.
 */
export const isBlockingTokenStatus = (status?: LinkProblem): boolean =>
	Boolean(status) && status !== TokenStatus.OPEN;

/**
 * What is wrong with the link the page was opened with, or null when the form may be shown: no
 * token in the URL is MISSING, any non-OPEN validation status is passed through.
 */
export const resolveLinkProblem = (token: string, status?: TokenStatus): LinkProblem | null => {
	if (!token) {
		return LINK_TOKEN_MISSING;
	}

	return isBlockingTokenStatus(status) ? (status ?? null) : null;
};

/**
 * A missing or unknown token has no resend path of its own (there is no token to resend), so the
 * page offers the forgotten-password form instead; an expired link keeps its one-click resend.
 */
export const canRequestNewLink = (problem: LinkProblem): boolean =>
	problem === LINK_TOKEN_MISSING || problem === TokenStatus.INVALID;

/**
 * Maps a raw token status to the user-facing error for the given flow. Shared by both the on-load
 * token validation and the post-submit response handling so the two never drift.
 */
export const getTokenStatusError = (
	status: LinkProblem | undefined,
	flow: SetPasswordFlow
): ERROR_TYPE | null => {
	if (!isBlockingTokenStatus(status)) {
		return null;
	}

	const errorMap = flow === "invitation" ? invitationStatusErrors : resetStatusErrors;

	return errorMap[status as keyof typeof errorMap] ?? ERROR_TYPE.SOMETHING_WRONG;
};

export const getSetPasswordTokenError = (
	response: SetPasswordResponse,
	flow: SetPasswordFlow
): ERROR_TYPE | null => {
	if (hasSetPasswordAuthPayload(response)) {
		return null;
	}

	return getTokenStatusError(response.status, flow);
};
